"use client";

import { useEffect } from "react";
import styles from "./LoginForm.module.css";
import { useClientTranslations } from "@/lib/translate";
import { Input } from "@/modules/components/commons/Input";
import { EyeIcon, ViewOffIcon } from "hugeicons-react";
import AppleLogo from "@/assets/imgs/apple-logo.svg";
import GoogleLogo from "@/assets/imgs/google-logo.svg";
import Link from "next/link";
import Image from "next/image";
import Checkbox from "@/modules/components/commons/Checkbox";
import { routes } from "@/lib/utils/routes";
import CircularProgress from "@/modules/components/commons/CircularProgress";
import { useRouter } from "next/navigation";
import { useLoginForm } from "@/modules/hooks/auth/useLoginForm";

export const LoginForm = () => {
  const { t } = useClientTranslations("login-page");
  const {
    formData,
    errors,
    isLoading,
    isShowPassword,
    isAuthenticated,
    onChangeEmail,
    onChangePassword,
    onToggleRememberMe,
    onToggleTerms,
    handleSubmit,
    toggleShowPassword,
    handleGoogleLogin,
  } = useLoginForm(t);
  const router = useRouter();

  useEffect(() => {
    if (isAuthenticated) {
      router.push(routes.home);
    }
  }, [isAuthenticated, router]);

  return (
    <div className="">
      <form onSubmit={handleSubmit} className={styles.form}>
        <h1 className="text-2xl font-bold mb-7 text-primary dark:text-white">
          {t("login.title")}
        </h1>
        <Input
          id="email"
          type="email"
          placeholder={t("login.email")}
          value={formData.email}
          onChange={(e) => onChangeEmail(e.target.value)}
          error={errors.email}
          required
        />
        <div className="relative">
          <Input
            id="password"
            type={isShowPassword ? "text" : "password"}
            placeholder={t("login.password")}
            value={formData.password}
            onChange={(e) => onChangePassword(e.target.value)}
            error={errors.password}
            required
          />
          <button
            type="button"
            className="absolute right-0 top-0 text-sm text-primary translate-y-1/3 translate-x-[-50%]"
            onClick={toggleShowPassword}
          >
            {isShowPassword ? <EyeIcon /> : <ViewOffIcon />}
          </button>
        </div>
        {errors.submit && (
          <p className="text-red-500 text-sm">{errors.submit}</p>
        )}
        <div className="flex flex-row gap-1">
          <Checkbox
            id="remember-me"
            checked={formData.isSaveLogin}
            onChange={onToggleRememberMe}
            label={t("login.rememberMe")}
          />
        </div>
        <div className="flex flex-row gap-3 items-center">
          <Checkbox
            id="terms"
            checked={formData.terms}
            onChange={onToggleTerms}
          />
          <p>
            {t("login.read")}{" "}
            <Link
              href={routes.about.term}
              className="text-primary font-semibold hover:underline"
            >
              {t("login.terms")}
            </Link>{" "}
            {t("login.and")}{" "}
            <Link
              href={routes.about.privacy}
              className="text-primary font-semibold hover:underline"
            >
              {t("login.privacy")}
            </Link>
          </p>
        </div>
        {errors.terms && <p className="text-red-500 text-sm">{errors.terms}</p>}
        <button
          type="submit"
          className="flex justify-center items-center bg-gradient-to-r from-purple-400 hover:from-purple-500 to-purple-600 hover:to-purple-700 focus:ring-purple-500 focus:outline-none focus:ring-4 text-white py-2 rounded-lg"
        >
          {isLoading ? <CircularProgress /> : t("login.button")}
        </button>
      </form>
      <div className="mt-7 grid grid-cols-1 gap-2 divide-y divide-gray-700 dark:divide-gray-100">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-1">
            <p className="text-sm">{t("login.registerQuestion")}</p>
            <Link className="text-primary text-sm" href={routes.register}>
              {t("login.register")}
            </Link>
          </div>
          <Link className="text-primary text-sm" href={routes.forgotPassword}>
            {t("login.forgotPassword")}
          </Link>
        </div>
        <div className="flex flex-row gap-1 pt-3">
          <button 
            type="button" 
            onClick={() => handleGoogleLogin()} 
            className="w-full rounded-lg bg-white p-1 flex justify-center items-center border-2 border-gray-300 dark:border-none"
          >
            <Image
              src={GoogleLogo}
              width={40}
              height={40}
              alt="Together Work Login Google"
            />
          </button>
          <button className="w-full rounded-lg bg-white p-1 flex justify-center items-center border-2 border-gray-300 dark:border-none">
            <Image
              src={AppleLogo}
              width={30}
              height={30}
              alt="Together Work Login Apple"
            />
          </button>
        </div>
      </div>
    </div>
  );
};
